import { useCallback, useRef } from 'react'
import type { PriceOverride } from '@/types/priceOverride'
import { formatPrice } from '@/utils/helpers'
import { cn } from '@/lib/utils'

const DAY_NAMES = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const

interface DefaultPrices {
  hourlyRate: number
  dailyRate: number
  overnightRate: number
  extraHourRate: number
  combo3hRate: number
  combo6h1hRate: number
  combo6h1hDiscount: number
}

interface PriceCalendarProps {
  year: number
  month: number // 1-12
  overrides: PriceOverride[]
  defaultPrices: DefaultPrices
  selectedDates: string[]
  onSelectDate: (date: string) => void
  onSelectRange: (dates: string[]) => void
}

/** Pad number to 2-digit string */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/** Format YYYY-MM-DD from parts */
function toDateStr(y: number, m: number, d: number): string {
  return `${y}-${pad2(m)}-${pad2(d)}`
}

/** Get today as YYYY-MM-DD using local timezone */
function todayStr(): string {
  const now = new Date()
  return toDateStr(now.getFullYear(), now.getMonth() + 1, now.getDate())
}

export function PriceCalendar({
  year,
  month,
  overrides,
  defaultPrices,
  selectedDates,
  onSelectDate,
  onSelectRange,
}: PriceCalendarProps) {
  const lastClickedRef = useRef<string | null>(null)
  const today = todayStr()

  // Build override lookup by date
  const overrideMap = new Map<string, PriceOverride>()
  for (const o of overrides) {
    overrideMap.set(o.date, o)
  }

  // Calendar grid math
  const firstDay = new Date(year, month - 1, 1)
  // getDay(): 0=Sun → shift to Mon-based: (day + 6) % 7
  const startOffset = (firstDay.getDay() + 6) % 7
  const daysInMonth = new Date(year, month, 0).getDate()

  const cells: (number | null)[] = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  // Pad trailing cells to complete the last row
  while (cells.length % 7 !== 0) cells.push(null)

  const handleClick = useCallback(
    (dateStr: string, e: React.MouseEvent) => {
      if (e.shiftKey && lastClickedRef.current) {
        // Range select
        const from = lastClickedRef.current
        const to = dateStr
        const fromDate = new Date(from)
        const toDate = new Date(to)
        const start = fromDate <= toDate ? fromDate : toDate
        const end = fromDate <= toDate ? toDate : fromDate
        const range: string[] = []
        const cursor = new Date(start)
        while (cursor <= end) {
          range.push(
            toDateStr(cursor.getFullYear(), cursor.getMonth() + 1, cursor.getDate()),
          )
          cursor.setDate(cursor.getDate() + 1)
        }
        onSelectRange(range)
      } else {
        onSelectDate(dateStr)
      }
      lastClickedRef.current = dateStr
    },
    [onSelectDate, onSelectRange],
  )

  return (
    <div className="select-none">
      {/* Header row */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {DAY_NAMES.map((name) => (
          <div
            key={name}
            className="text-center text-xs font-semibold text-muted-foreground py-2"
          >
            {name}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-px">
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="h-20" />
          }

          const dateStr = toDateStr(year, month, day)
          const override = overrideMap.get(dateStr)
          const isPast = dateStr < today
          const isSelected = selectedDates.includes(dateStr)
          const hourly = override?.hourlyRate ?? defaultPrices.hourlyRate
          const isRoomSpecific = override?.roomId != null
          const isTypeLevel = override != null && !isRoomSpecific

          return (
            <button
              key={dateStr}
              type="button"
              onClick={(e) => handleClick(dateStr, e)}
              className={cn(
                'relative h-20 p-1.5 text-left border rounded-md transition-colors',
                'hover:bg-accent/50 cursor-pointer',
                // Color coding
                !override && 'bg-white border-border',
                isTypeLevel && 'bg-blue-50 border-blue-200',
                isRoomSpecific && 'bg-green-50 border-green-200',
                // Selection ring
                isSelected && 'ring-2 ring-primary',
                // Past date opacity
                isPast && 'opacity-50',
              )}
            >
              <span className="text-sm font-medium">{day}</span>
              <div className="mt-1 text-[11px] text-muted-foreground leading-tight truncate">
                {formatPrice(hourly)}đ/h
              </div>
              {override && (
                <div className="absolute top-1 right-1.5">
                  <span
                    className={cn(
                      'inline-block w-1.5 h-1.5 rounded-full',
                      isRoomSpecific ? 'bg-green-500' : 'bg-blue-500',
                    )}
                  />
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
